import { Avatar, Box, Button, Card, CardContent, CircularProgress, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ToastOptions, toast } from 'react-toastify';
import { epcClient } from '../../api/client';
import QrScan from '../../component/qrScan/QrScan';
import useAppDispatch from '../../hooks/useAppDispatch';
import {
  deliverAssignment,
  getAssignment,
  getAssignmentById,
  getVehicleManifest,
  lookupAssignmentByItem,
} from '../../redux/slices/assignments/assignmentsSlice';
import CategoryCountWidget from './CategoryCountWidget';

const toastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'dark',
} as ToastOptions;

const lookupErrorText = (rejection: unknown): string => {
  const messages = (rejection as { messages?: string[] })?.messages;
  const joined = Array.isArray(messages) ? messages.join(' ') : '';
  if (joined.includes('Not in a prepared assignment')) {
    return 'This item is not in a prepared assignment.';
  }
  if (joined.includes('Item not found')) {
    return 'Item not found.';
  }
  return 'Unable to look up the scanned item. Please try again.';
};

export const Deliver = (): ReactElement => {
  const dispatch = useAppDispatch();
  const { assignmentId: routeAssignmentId } = useParams();
  const [showQr, setShowQr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignmentId, setAssignmentId] = useState<string | undefined>(routeAssignmentId);
  const assignment = useSelector(getAssignment(assignmentId));
  const [delivering, setDelivering] = useState(false);
  const [deliverResult, setDeliverResult] = useState<string | null>(null);
  const [deliverError, setDeliverError] = useState<string | null>(null);

  // Entry from a home-page manifest card: load the assignment without a scan.
  useEffect(() => {
    if (routeAssignmentId) {
      setAssignmentId(routeAssignmentId);
      dispatch(getAssignmentById(routeAssignmentId));
    }
  }, [dispatch, routeAssignmentId]);

  const checkScanResult = async (decodedText: string) => {
    try {
      const response = await epcClient.validateEpc(decodedText);
      if (!response.data.result && decodedText.length >= 12) {
        toast.error(`"${decodedText}" is Invalid EPC.`, toastOptions);
        return;
      }
    } catch {
      // Format validation is best-effort; the lookup below authoritatively rejects unknown identifiers.
    }

    const identifier = decodedText.replace(/^0[xX]/, '');
    setLoading(true);
    setError(null);
    setAssignmentId(undefined);
    setDeliverResult(null);
    setDeliverError(null);
    try {
      const result = await dispatch(lookupAssignmentByItem({ identifier, options: { hideError: true } })).unwrap();
      setAssignmentId(result.data.result?.id);
    } catch (rejection) {
      setError(lookupErrorText(rejection));
    } finally {
      setLoading(false);
    }
  };

  const handleDeliver = async () => {
    if (!assignmentId) return;
    setDelivering(true);
    setDeliverError(null);
    try {
      await dispatch(deliverAssignment(assignmentId)).unwrap();
      const count = assignment?.items?.length ?? 0;
      setDeliverResult(`Delivered — ${count} item${count === 1 ? '' : 's'} left onsite`);
      dispatch(getVehicleManifest({ hideError: true }));
    } catch (rejection) {
      const status = (rejection as { status?: number })?.status;
      if (status === 409) {
        const messages = (rejection as { messages?: string[] })?.messages;
        const joined = Array.isArray(messages) ? messages.join(' ') : '';
        setDeliverError(joined.includes('possession') ? 'This assignment is not in your possession.' : 'The job site is disabled.');
      } else if (status === 404) {
        setDeliverError('No prepared assignment found — it may already be delivered.');
      } else {
        setDeliverError('Unable to deliver. Please try again.');
      }
    } finally {
      setDelivering(false);
    }
  };

  return (
    <>
      <Helmet title="Deliver" />
      <Stack spacing={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h4">Deliver</Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowQr(true)}
            disabled={loading}
            startIcon={<Box component="i" className="tags-icon-barcode" />}
            sx={{ minHeight: 48 }}
          >
            {assignment || error ? 'Scan Another' : 'Scan Item'}
          </Button>
        </Box>

        {!assignment && !error && !loading && (
          <Typography color="text.secondary">Scan one item from your truck to deliver the whole assignment to its job site.</Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Card>
            <CardContent>
              <Typography color="error" gutterBottom>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check the tag and scan again.
              </Typography>
            </CardContent>
          </Card>
        )}

        {assignment && !loading && (
          <>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {assignment.job_site_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[assignment.job_site_address, assignment.job_site_city, assignment.job_site_state, assignment.job_site_postal].filter(Boolean).join(', ')}
                </Typography>
                {assignment.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {assignment.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <CategoryCountWidget items={assignment.items} title="To Deliver" />

            <Card>
              <List dense>
                {(assignment.items ?? []).map((item) => (
                  <ListItem key={item.item_id}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={item.image_src || undefined}>
                        <Box component="i" className="tags-icon-barcode" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={item.product_name} secondary={[item.manufacturer_name, item.identifier].filter(Boolean).join(' — ')} />
                  </ListItem>
                ))}
              </List>
            </Card>

            <Box>
              {deliverResult ? (
                <Card>
                  <CardContent>
                    <Typography color="success.main">{deliverResult}</Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Button fullWidth variant="contained" size="large" sx={{ minHeight: 48 }} disabled={delivering} onClick={handleDeliver}>
                    {delivering ? 'Delivering…' : `Deliver to ${assignment.job_site_name ?? 'Job Site'}`}
                  </Button>
                  {deliverError && (
                    <Typography color="error" sx={{ mt: 2 }}>
                      {deliverError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Stack>

      <QrScan open={showQr} onClose={() => setShowQr(false)} checkScanResult={checkScanResult} />
    </>
  );
};

export default Deliver;
